/**
 * SOLID Review
 * S — Strong. One concern: the two stores, shaped — and normalised — as the session the decisions read,
 *     and the session's changes split back into each store's own fields.
 * O — Good. A new session field is one line in each direction.
 * L — N/A — functions.
 * I — Strong. Three exports: the projection, its inverse, and the view React reads.
 * D — Strong. The stores' types (declared in application/state), the Domain's schemas and its two view
 *     rules (`adZoneOf`, FE4d; `mediaFor`, FE5c).
 */
import { MonetizationTrack } from '../../domain/uml-components/primitives';
import type { GameStore, TGameStoreData } from './gameStoreTypes';
import type { IStateSessionHost } from './sessionStoreTypes';
import type { IStateClientSession, IPropsClientSessionChanges, IEntityClientPlayer, TClientScreen } from '../../domain/ports/schemas/ClientSession';
import type { IEntityLocalUser } from '../../domain/uml-components/entities';
import { ClientConsent, type IEntityClientConsent } from '../../domain/ports/schemas/ConsentMoves';
import { INITIAL_CONSENT_STATE } from '../../domain/ports/IStateCookieConsent';
import { ClientAds, NO_ADS, type IEntityClientAds } from '../../domain/ports/schemas/AdsMoves';
import { adZoneOf } from '../../domain/ads/adZone';
import { ClientMedia, NO_MEDIA, type IEntityClientMedia } from '../../domain/ports/schemas/MediaMoves';
import { mediaFor } from '../../domain/decisions/media/mediaPolicy';
import type { IEntitySessionView } from '../ports/ClientStateView';

/** No decision yet: everything denied but functionality, advertising not allowed, nothing recorded. */
const NO_CONSENT: IEntityClientConsent = {
  resolved: INITIAL_CONSENT_STATE.resolved,
  categories: INITIAL_CONSENT_STATE.categories,
  region: INITIAL_CONSENT_STATE.region,
  source: INITIAL_CONSENT_STATE.source,
  advertisingAllowed: false,
  recordedSignature: null,
};

/** The stored consent, or none: a malformed one reads as no decision, never as consent granted. */
function consentOf(value: unknown): IEntityClientConsent {
  const parsed = ClientConsent.safeParse(value);
  return parsed.success ? parsed.data : NO_CONSENT;
}

/** The stored ad config, or none asked for yet: a malformed one reads as the defaults. */
function adsOf(value: unknown): IEntityClientAds {
  const parsed = ClientAds.safeParse(value);
  return parsed.success ? parsed.data : NO_ADS;
}

/** The stored media state, or none: a malformed one reads as video chat off at no table. */
function mediaOf(value: unknown): IEntityClientMedia {
  const parsed = ClientMedia.safeParse(value);
  return parsed.success ? parsed.data : NO_MEDIA;
}

const SCREENS: readonly string[] = Object.freeze<TClientScreen[]>([
  'WELCOME', 'ENTER_CODE', 'GUEST_CHOICE', 'VERIFY_EMAIL', 'LOGIN', 'VERIFY_LOGIN', 'PAYMENT_SUCCESS', 'GAME',
]);

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function int(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : 0;
}

function playerOf(user: IEntityLocalUser | null | undefined): IEntityClientPlayer | null {
  if (user === null || user === undefined || typeof user.playerId !== 'string' || user.playerId === '') return null;
  return {
    playerId: user.playerId,
    displayName: typeof user.displayName === 'string' ? user.displayName : '',
    matchCredits: int(user.matchCredits),
    isAdmin: user.isAdmin === true,
  };
}

/**
 * THE SESSION AS THE DECISIONS SEE IT (frontend strict-ports plan, FE2, 2026-10-03), from the auth store
 * (account, screen, pending join) and the game store (the seated player, the overlay, the entry and the
 * auto-host). NORMALISED as FE1's table projection is, and for the same reason: until FE4 and FE6 some
 * screens still write these stores directly, and one stray value must not stop every decision.
 *
 * The account is `registered` because the auth store says so — since FE3 the store holds no token at
 * all; the JWT lives in Infrastructure's token holder.
 */
export function projectClientSession(game: TGameStoreData, auth: IStateSessionHost): IStateClientSession {
  const kind = auth.registered === true ? 'registered' : auth.isGuest === true ? 'guest' : 'anonymous';
  const session: IStateClientSession = {
    account: {
      kind,
      playerId: textOrNull(auth.playerId),
      displayName: textOrNull(auth.displayName),
      email: textOrNull(auth.email),
      matchCredits: int(auth.matchCredits),
    },
    player: playerOf(game.localUser),
    screen: SCREENS.includes(auth.screen) ? auth.screen : 'WELCOME',
    pendingRoomCode: textOrNull(auth.pendingRoomCode),
    hostOnEntry: auth.hostOnEntry === true,
    pendingJoinError: textOrNull(auth.pendingJoinError),
    pendingEmail: textOrNull(auth.pendingEmail),
    consent: consentOf(auth.consent),
    ads: adsOf(auth.ads),
    media: mediaOf(auth.media),
    restoring: game.isRestoringSession === true,
    entering: game.entering === true,
    hosting: game.hosting === true,
    hostError: textOrNull(game.hostError),
  };
  return session;
}

/** The session's changes, as each store holds them. */
export interface IEntitySessionWrites {
  readonly game: Partial<GameStore>;
  readonly auth: Partial<IStateSessionHost>;
}

/**
 * The inverse: which store each changed field lives in. The seated player becomes the game store's
 * `localUser`, its tier following its credits. (A screen change cleared the auth screens' shared error
 * line until FE3; each auth screen holds its own message now.)
 */
export function sessionWrites(changes: IPropsClientSessionChanges): IEntitySessionWrites {
  const game: Partial<GameStore> = {};
  const auth: Partial<IStateSessionHost> = {};
  if (changes.account !== undefined) {
    const { kind, playerId, displayName, email, matchCredits } = changes.account;
    Object.assign(auth, { playerId, displayName, email, matchCredits });
    auth.registered = kind === 'registered';
    auth.isGuest = kind === 'guest';
  }
  if (changes.player !== undefined) {
    const p = changes.player;
    game.localUser = p === null ? null : {
      playerId: p.playerId,
      displayName: p.displayName,
      email: null,
      avatarUrl: null,
      matchCredits: p.matchCredits,
      monetizationTrack: p.matchCredits > 0 ? MonetizationTrack.PREMIUM : MonetizationTrack.FREE,
      sessionToken: null,
      isAdmin: p.isAdmin,
    };
  }
  if (changes.screen !== undefined) auth.screen = changes.screen;
  if (changes.pendingRoomCode !== undefined) auth.pendingRoomCode = changes.pendingRoomCode;
  if (changes.hostOnEntry !== undefined) auth.hostOnEntry = changes.hostOnEntry;
  if (changes.pendingJoinError !== undefined) auth.pendingJoinError = changes.pendingJoinError;
  if (changes.pendingEmail !== undefined) auth.pendingEmail = changes.pendingEmail;
  if (changes.consent !== undefined) auth.consent = changes.consent;
  if (changes.ads !== undefined) auth.ads = changes.ads;
  if (changes.media !== undefined) auth.media = changes.media;
  if (changes.restoring !== undefined) game.isRestoringSession = changes.restoring;
  if (changes.entering !== undefined) game.entering = changes.entering;
  if (changes.hosting !== undefined) game.hosting = changes.hosting;
  if (changes.hostError !== undefined) game.hostError = changes.hostError;
  const writes: IEntitySessionWrites = { game, auth };
  return writes;
}

/**
 * The session as React reads it: the projection's fields a screen reads — never the token — the ad zone,
 * by the Domain's rule (FE4d), and the current table's video chat (FE5c), both computed here rather than
 * kept, so a reset that unseats the player or empties the table cannot leave them stale.
 */
export function sessionViewOf(game: TGameStoreData, auth: IStateSessionHost): IEntitySessionView {
  const projected = projectClientSession(game, auth);
  const view: IEntitySessionView = {
    account: projected.account,
    screen: projected.screen,
    pendingRoomCode: projected.pendingRoomCode,
    hostOnEntry: projected.hostOnEntry,
    pendingJoinError: projected.pendingJoinError,
    pendingEmail: projected.pendingEmail,
    consent: projected.consent,
    ads: projected.ads,
    adZone: adZoneOf(projected),
    // The current table's video chat: a state kept for another room (or none) reads as fresh (FE5c).
    media: mediaFor(projected, game.room?.roomId ?? null),
  };
  return view;
}
